import {
  createSystem,
  defaultConfig,
  defineRecipe,
  defineSlotRecipe,
  defineTokens,
} from '@chakra-ui/react'
import { useTheme as useNextThemes } from 'next-themes'

export const colors = {
  black: { value: '#171717' },
  primary: {
    50: { value: '#e8ecff' },
    100: { value: '#e8ecff' },
    200: { value: '#c0c5f6' },
    300: { value: '#969fea' },
    400: { value: '#6c79df' },
    500: { value: '#254FE5' },
    600: { value: '#2a39bb' },
    700: { value: '#202c93' },
    800: { value: '#151f6a' },
    900: { value: '#0a1342' },
  },
  secondary: {
    50: { value: '#adfff6' },
    100: { value: '#7dfff0' },
    200: { value: '#4dffea' },
    300: { value: '#26ffe4' },
    400: { value: '#15e6cb' },
    500: { value: '#00BBA5' },
    600: { value: '#008071' },
    700: { value: '#004e44' },
    800: { value: '#001c18' },
    900: { value: '#000c18' },
  },
  warning: {
    50: { value: '#fef6ea' },
    100: { value: '#fcdcab' },
    200: { value: '#f9ba5b' },
    300: { value: '#ee8f00' },
    400: { value: '#d58000' },
    500: { value: '#b46c00' },
    600: { value: '#985b00' },
    700: { value: '#7a4900' },
    800: { value: '#673e00' },
    900: { value: '#4b2d00' },
  },
  danger: {
    50: { value: '#FFEEF2' },
    100: { value: '#ffe4eb' },
    200: { value: '#fcb8c6' },
    300: { value: '#f48c9f' },
    400: { value: '#ed5e79' },
    500: { value: '#e73153' },
    600: { value: '#ce1839' },
    700: { value: '#a1112d' },
    800: { value: '#73091f' },
    900: { value: '#480312' },
  },
  branding: {
    catYellow: { value: '#FC0' },
  },
}

const semanticTokens = {
  colors: {
    border: { value: { base: 'rgba(23, 23, 23, 0.1)', _dark: 'rgba(255, 255, 255, 0.16)' } },
    primary: {
      solid: { value: '{colors.primary.500}' },
      contrast: { value: '{colors.primary.100}' },
      fg: { value: '{colors.primary.700}' },
      muted: { value: '{colors.primary.100}' },
      subtle: { value: '{colors.primary.200}' },
      emphasized: { value: '{colors.primary.300}' },
      focusRing: { value: '{colors.primary.500}' },
    },
    secondary: {
      solid: { value: '{colors.secondary.500}' },
      contrast: { value: '{colors.secondary.100}' },
      fg: { value: '{colors.secondary.700}' },
      muted: { value: '{colors.secondary.100}' },
      subtle: { value: '{colors.secondary.200}' },
      emphasized: { value: '{colors.secondary.300}' },
      focusRing: { value: '{colors.secondary.500}' },
    },
    black: {
      solid: { value: '{colors.black}' },
      contrast: { value: '{colors.white}' },
      fg: { value: '{colors.black}' },
      muted: { value: '{colors.black}' },
      subtle: { value: '{colors.black}' },
      emphasized: { value: '{colors.black}' },
      focusRing: { value: '{colors.black}' },
    },
  },
}

const borderRadiusTokens = {
  none: { value: '0' },
  xs: { value: '5px' },
  sm: { value: '10px' },
  base: { value: '12px' },
  md: { value: '14px' },
  lg: { value: '18px' },
  xl: { value: '22px' },
  '2xl': { value: '28px' },
  '3xl': { value: '36px' },
  full: { value: '9999px' },
}

const fontSizeTokens = {
  xs: { value: '12px' },
  sm: { value: '14px' },
  md: { value: '16px' },
  lg: { value: '18px' },
  xl: { value: '20px' },
  '2xl': { value: '22px' },
  '3xl': { value: '28px' },
  '4xl': { value: '32px' },
  '5xl': { value: '36px' },
  '6xl': { value: '44px' },
}

const sizeTokens = {
  1: { value: '0.25rem' },
  2: { value: '0.5rem' },
  3: { value: '0.75rem' },
  4: { value: '1rem' },
  5: { value: '1.25rem' },
  6: { value: '1.5rem' },
  7: { value: '1.75rem' },
  8: { value: '2rem' },
  9: { value: '2.25rem' },
  10: { value: '2.5rem' },
  11: { value: '2.75rem' },
  12: { value: '3rem' },
  14: { value: '3.5rem' },
  16: { value: '4rem' },
  20: { value: '5rem' },
}

export enum borderRadiusMap {
  none = '0',
  xs = '5px',
  sm = '10px',
  base = '12px',
  md = '14px',
  lg = '18px',
  xl = '22px',
  '2xl' = '28px',
  '3xl' = '36px',
  full = '9999px',
}

export enum fontSizeMap {
  xs = '12px',
  sm = '14px',
  md = '16px',
  lg = '18px',
  xl = '20px',
  '2xl' = '22px',
  '3xl' = '28px',
  '4xl' = '32px',
  '5xl' = '36px',
  '6xl' = '44px',
}

export const system = createSystem(defaultConfig, {
  theme: {
    tokens: defineTokens({
      colors,
      radii: borderRadiusTokens,
      fontSizes: fontSizeTokens,
      sizes: sizeTokens,
      // borders: {
      //   subtle: { value: '1px solid red' },
      // },
    }),
    semanticTokens,
    recipes: {
      button: defineRecipe({
        base: { borderRadius: 'md', transition: 'all 0.2s' },
        variants: {
          size: {
            xs: { h: '{sizes.6}', minW: '{sizes.6}', fontSize: 'xs', px: '{sizes.2}' },
            sm: { h: '{sizes.8}', minW: '{sizes.8}', fontSize: 'xs', px: '{sizes.3}' },
            md: { h: '{sizes.10}', minW: '{sizes.10}', fontSize: 'sm', px: '{sizes.4}' },
            lg: { h: '{sizes.12}', minW: '{sizes.12}', fontSize: 'md', px: '{sizes.6}' },
            xl: { h: '{sizes.14}', minW: '{sizes.14}', fontSize: 'lg', px: '{sizes.6}' },
          },
        },
      }),
    },
    slotRecipes: {
      avatar: defineSlotRecipe({
        slots: ['root', 'image'],
        base: {
          image: { height: '100%', width: '100%', borderRadius: 'full' },
        },
        variants: {
          size: {
            xs: { root: { height: '{sizes.6}', width: '{sizes.6}' }, fallback: { fontSize: 'xs' } },
            sm: { root: { height: '{sizes.8}', width: '{sizes.8}' }, fallback: { fontSize: 'sm' } },
            md: { root: { height: '3rem', width: '3rem' }, fallback: { fontSize: '1.5rem' } },
            lg: { root: { height: '4rem', width: '4rem' }, fallback: { fontSize: '1.9rem' } },
            xl: { root: { height: '6rem', width: '6rem' }, fallback: { fontSize: '2.8rem' } },
            '2xl': { root: { height: '8rem', width: '8rem' }, fallback: { fontSize: '4rem' } },
          },
        },
      }),
      numberInput: defineSlotRecipe({
        slots: ['root'],
        base: {
          root: {
            background: { _light: 'white', _dark: 'rgba(255, 255, 255, 0.05)' },
            borderColor: { _light: 'rgba(23, 23, 23, 0.2)', _dark: 'rgba(255, 255, 255, 0.2)' },
          },
        },
      }),
    },
    textStyles: {
      h1: {
        value: {
          fontSize: fontSizeMap['6xl'],
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-1.2px',
        },
      },
      h2: {
        value: {
          fontSize: fontSizeMap['5xl'],
          fontWeight: 'semibold',
          lineHeight: 'normal',
        },
      },
      h3: {
        value: {
          fontSize: fontSizeMap['4xl'],
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-1.6px',
        },
      },
      h4: {
        value: {
          fontSize: fontSizeMap['3xl'],
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-1.4px',
        },
      },
      h5: {
        value: {
          fontSize: fontSizeMap['2xl'],
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-1.1px',
        },
      },
      h6: {
        value: {
          fontSize: fontSizeMap['xl'],
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-1px',
        },
      },
      bodyLarge: {
        value: {
          fontSize: fontSizeMap.lg,
          fontWeight: '600',
          lineHeight: 'normal',
          letterSpacing: '-0.32px',
        },
      },
      bodyMedium: {
        value: {
          fontSize: fontSizeMap.md,
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-0.32px',
        },
      },
      bodySmall: {
        value: {
          fontSize: fontSizeMap.sm,
          fontWeight: 'semibold',
          lineHeight: 'normal',
          letterSpacing: '-0.28px',
        },
      },
      bodyXSmall: {
        value: {
          fontSize: fontSizeMap.xs,
          fontWeight: '500',
          lineHeight: 'normal',
          letterSpacing: '-0.12px',
        },
      },
    },
  },
})

export function useTheme() {
  const { theme, setTheme, forcedTheme } = useNextThemes()
  return {
    theme,
    setTheme,
    forcedTheme,
  }
}

export const cssVariables = {
  dark: {
    '--chakra-colors-bg-panel': 'colors.gray.700',
    '--chakra-colors-border': 'rgba(255, 255, 255, 0.2)',
    '--chakra-colors-color-palette-contrast': 'colors.white',
  },
  light: {
    '--chakra-colors-border': 'rgba(23, 23, 23, 0.2)',
    '--chakra-colors-color-palette-contrast': 'colors.black',
  },
}

export const forceLight = {
  color: 'black',
  className: 'chakra-theme light',
  colorScheme: 'light',
  css: cssVariables.light,
}

export const forceDark = {
  color: 'white',
  className: 'chakra-theme dark',
  colorScheme: 'dark',
  css: cssVariables.dark,
}
